import { TOTALS_COLUMN_VALUE_KEY } from '../../constants';
import type { ColumnValueSegment } from '../base';

/**
 * ASCII Unit Separator. Chosen because ERP data does not produce control characters, so the
 * common case needs no escaping and saved report configs stay readable.
 */
export const COLUMN_PATH_DELIMITER = '\u001F';

const ESCAPE = '\\';

function escapeSegment(segment: string): string {
  return segment
    .split(ESCAPE)
    .join(ESCAPE + ESCAPE)
    .split(COLUMN_PATH_DELIMITER)
    .join(ESCAPE + COLUMN_PATH_DELIMITER);
}

function splitEscaped(key: string): string[] {
  const segments: string[] = [];
  let current = '';
  let index = 0;

  while (index < key.length) {
    const char = key[index];
    if (char === ESCAPE && index + 1 < key.length) {
      current += key[index + 1];
      index += 2;
    } else if (char === COLUMN_PATH_DELIMITER) {
      segments.push(current);
      current = '';
      index += 1;
    } else {
      current += char;
      index += 1;
    }
  }

  segments.push(current);
  return segments;
}

/**
 * Encodes a column's level path into the single string used as its AG Grid field key, its
 * `columnValueMapping` key and its calculated-column operand.
 *
 * A one-segment path is returned RAW, with no escaping. That is deliberate and load-bearing:
 * every key persisted before multi-level support is a one-segment path, so this guarantees
 * existing renames and calc-column references keep resolving with no migration.
 *
 * The one exception is a single segment that itself contains the delimiter: returned raw it would be
 * byte-identical to a two-segment path, so one column could resolve to another's data. Such a
 * segment is escaped, which keeps the round trip exact and leaves every delimiter-free key — that
 * is, every key this has ever produced in practice — unchanged.
 */
export function buildColumnPathKey(segments: ReadonlyArray<ColumnValueSegment>): string {
  const normalized = segments.map((segment) =>
    segment === null || segment === undefined ? TOTALS_COLUMN_VALUE_KEY : String(segment)
  );
  if (normalized.length === 0) return TOTALS_COLUMN_VALUE_KEY;
  if (normalized.length === 1) {
    return normalized[0].includes(COLUMN_PATH_DELIMITER) ? escapeSegment(normalized[0]) : normalized[0];
  }
  return normalized.map(escapeSegment).join(COLUMN_PATH_DELIMITER);
}

/**
 * Inverse of `buildColumnPathKey`.
 *
 * A key with no delimiter is a one-segment path and is returned as-is, matching the raw
 * (unescaped) encoding `buildColumnPathKey` produces for that case.
 */
export function parseColumnPathKey(key: string): string[] {
  if (!key.includes(COLUMN_PATH_DELIMITER)) return [key];
  return splitEscaped(key);
}
